<template>
  <div
    class="app"
    :class="[
      theme,
      { background: showBackground },
      { 'hide-cursor': isTerminalChannel && !terminalMouseCursorEnabled },
    ]"
  >
    <router-view />
    <TicketsHistory v-if="user.profile.id" />
    <Modal
      v-if="ticketCanceled"
      ref="modal"
      show-footer
      class="cancel-ticket-confirm"
      html-id="cancel-confirm-modal"
      @close-modal="closeCancelTicketModal"
      @close-modal-outside="closeCancelTicketModal('outside')"
    >
      <template #header>
        {{ translations.general_info }}
      </template>
      <template #body>
        {{ ticketCanceledMessage }}
      </template>
      <template #footer>
        <button class="button" @click="closeCancelTicketModal">{{ translations.general_ok }}</button>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetModal"
      ref="modal"
      class="free-bet-modal"
      :class="[{ mobile: isMobile }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="claimFreeBets()"
      @close-modal-outside="claimFreeBets('outside')"
    >
      <template #body>
        <img src="./assets/images/free-bet-star.png" alt="" />
        <div class="body-message-box">
          <div class="pre-title">{{ translations.general_free_bet_pre_title.toUpperCase() }}</div>
          <div class="title">{{ translations.general_free_bet }}</div>
          <div class="subtitle">{{ translations.general_congratulation.toUpperCase() }}</div>
          <span class="subtitle-2">{{ translations.general_claim_ticket }}</span>
        </div>
        <div class="action">
          <button class="button" @click="claimFreeBets">
            {{ claimFreeBetsLabel }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetExpiredModal"
      ref="modal"
      class="free-bet-expired-modal"
      :class="[{ mobile: isMobile }, { desktop: isDesktop }]"
      html-id="free-bet-expired-confirm-modal"
      width="344px"
      height="348px"
      show-footer
      @close-modal="toggleFreeBetExpiredModal(false)"
      @close-modal-outside="toggleFreeBetExpiredModal('outside')"
    >
      <template #header>
        {{ translations.general_info }}
      </template>
      <template #body>
        {{ freeBetExpiredMessage }}
      </template>
      <template #footer>
        <button class="button" @click="toggleFreeBetExpiredModal(false)">{{ translations.general_ok }}</button>
      </template>
    </Modal>
    <LandscapeNotification />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import applyPalette from '@nsftx/games-sdk-js/src/utility/applyPalette';
import { busService, integrator, webViewIntegration, terminalTicketHelper } from './utility';
import { merge, has, isArray, clone, startCase, isNil, cloneDeep } from 'lodash';
import eventBus from '@nsftx/games-sdk-js/src/utility/eventBus';
import gtag from '@nsftx/games-sdk-js/src/utility/gtag';
import { ulid } from 'ulid';
import NoSleep from 'nosleep.js';
import { defineAsyncComponent } from 'vue';
import { ticketAPI } from './api';

export default {
  name: 'App',
  components: {
    TicketsHistory: defineAsyncComponent(
      () => import(/* webpackChunkName: "ticketsHistory" */ './components/TicketsHistory'),
    ),
    Modal: defineAsyncComponent(
      () => import(/* webpackChunkName: "modal" */ '@nsftx/games-sdk-js/src/components/Modal'),
    ),
    LandscapeNotification: defineAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "LandscapeNotification" */ '@nsftx/games-sdk-js/src/components/LandscapeNotification'
        ),
    ),
  },
  data() {
    return {
      isLoggedIn: false,
      ticketCanceled: false,
      canceledTicket: {},
      check: null,
      checkTimeout: 10000,
      userFreeBets: {
        count: 0,
        betStake: 0,
      },
      payinTimoutWithoutTicketCheck: null,
    };
  },
  computed: {
    ...mapState([
      'gameState',
      'bettingRules',
      'isMobile',
      'isDesktop',
      'device',
      'isTablet',
      'jackpotState',
      'activeComponent',
      'ticketDetailsData',
      'printedTicketsByTicketCheck',
      'config',
      'roundId',
      'showFreeBetModal',
      'showFreeBetExpiredModal',
      'terminalMouseCursorEnabled',
      'nextgenStandaloneIntegration',
    ]),
    ...mapGetters([
      'translations',
      'isIsonis',
      'getNumberColor',
      'isTerminalChannel',
      'isTerminalLayout',
      'ajsDisabled',
      'theme',
      'isNextGen',
    ]),
    ...mapGetters('gamesBetslip', ['ticket', 'totalPayment', 'selectedBet']),
    ...mapGetters('user', ['user']),
    showBackground() {
      if (this.isTerminalLayout) {
        return this.activeComponent === 'BettingArea';
      }
      return !this.isMobile;
    },
    isAndroidPlatform() {
      return webViewIntegration.isActive() && has(window, 'WebAppListener');
    },
    ticketCanceledMessage() {
      return (
        this.translations.general_ticket +
        ' ' +
        this.canceledTicket.id +
        ' ' +
        `${this.translations.general_successfully_cancelled}!`
      );
    },
    isStandAlone() {
      return this.config.ui.config.mode === 'standalone';
    },
    isGravityGateway() {
      return this.config.messageProtocol.name === 'gravityGateway';
    },
    claimFreeBetsLabel() {
      if (this.isMobile) {
        return this.translations.general_claim;
      }
      return this.translations.general_ok;
    },
    isSevenPlatform() {
      return this.config.platformName === 'seven';
    },
    isNextGenPlatform() {
      return this.config.platformName === 'NextGen';
    },
    userLogged() {
      return this.user.profile.logged || this.isLoggedIn;
    },
    freeBetExpiredMessage() {
      return this.translations.general_free_bet_expired_message;
    },
  },
  watch: {
    roundId(newValue) {
      this.updateTicketRound(newValue);
    },
    userLogged(value) {
      if (value && !this.isTerminalChannel) {
        this.getLastTickets();
      }
    },
  },
  mounted() {
    if (!this.isDesktop) {
      const noSleep = new NoSleep();
      let wakeLockEnabled = false;
      document.addEventListener(
        'click',
        function enableNoSleep() {
          document.removeEventListener('click', enableNoSleep, false);
          if (!wakeLockEnabled) {
            noSleep.enable();
            wakeLockEnabled = true;
          } else {
            noSleep.disable();
            wakeLockEnabled = false;
          }
        },
        false,
      );
    }
    (async () => {
      if (!this.ajsDisabled) {
        await this.setJackpotState();
        this.addAjsChannel();
      }
    })();
    this.load();
    const device = this.isMobile ? 'mobile' : this.isTablet ? 'tablet' : 'desktop';
    this.setupGtag(device);
    if (this.config.user && this.config.user.id && !this.isTerminalChannel && !this.isNextGenPlatform) {
      this.authUser(this.config.user);
      this.setUserBalance(this.config.user.balance);
    }
    this.setTheme();
    this.setPageTitle();
    if (this.isTerminalChannel) {
      this.toggleDeviceChannel(this.device.deviceUuid);
      setInterval(this.timerTick, 1000);
    } else {
      if (this.config.platformConfig.clientType) {
        this.togglePlayerChannel(this.user.profile.id);
        this.getLastTickets();
      }
    }
    window.addEventListener('Bus:Message', (event) => {
      const { eventName } = event.detail;
      const eventDetail = event.detail;
      this.setIsBettingDisabled(false);
      switch (eventName) {
        case 'SetState':
          this.handleSetState(eventDetail);
          break;
        case 'StartEvent':
          this.handleStartEvent(eventDetail);
          break;
        case 'DrawResult': {
          this.drawResult(eventDetail);
          break;
        }
        case 'StopBetting':
        case 'onBettingDenied':
          this.handleStopBetting();
          break;
        case 'SetResults':
          this.handleSetResults(eventDetail);
          break;
        case 'StartCountdown':
          this.handleStartCountdown(eventDetail);
          break;
        case 'TicketUpdate':
          this.handleTicketUpdate(eventDetail);
          break;
        case 'User.AuthorizationChanged': {
          this.handleAuthorizationChanged(eventDetail);
          break;
        }
        case 'User.ProfileChanged':
          this.setUserBalance(eventDetail.data.profile.balance);
          break;
        case 'User.SettingsChanged':
          if (!this.userLogged) {
            this.resetUserFreeBets();
            this.clearBetslip();
            this.setUserFreeBets(this.userFreeBets);
          } else {
            this.getUserFreeBets();
          }
          break;
        case 'User.BalanceChanged':
          this.setUserBalance(eventDetail.data.balance);
          break;
        case 'FreeBet':
        case 'freebet':
          this.userFreeBets = eventDetail;
          // eslint-disable-next-line no-case-declarations
          const { subtype } = eventDetail;
          if (subtype === 'new') {
            this.toggleFreeBetModal(true);
          } else {
            if (this.user.freeBets.count > 0) {
              this.toggleFreeBetExpiredModal(true);
            }
            this.getUserFreeBets();
          }
          break;
        case 'balanceUpdate': {
          const balanceList = eventDetail.balanceArray;
          if (balanceList.length && !isNil(balanceList[0].balanceAmt)) {
            this.setUserBalance(balanceList[0].balanceAmt);
          }
          break;
        }
        // IGNITE
        case 'onSettingsUpdated':
          if (this.config.locale !== eventDetail.data.activeLocale) {
            this.updateConfig({ locale: eventDetail.data.activeLocale });
            // TODO HANDLE TRANSLATIONS CHANGE
          }
          if (this.config.currency !== eventDetail.data.activeCurrencyBalance) {
            this.updateConfig({ currency: eventDetail.data.activeCurrency.code });
          }
          break;
        case 'onBettingAllowed':
          this.setIsBettingDisabled(false);
          this.setIsPayinButtonDisabled(false);
          break;
        case 'onCancelBetsRequested':
          this.cancelTicket({
            id: eventDetail.data.betIds[0],
            providerBetslipId: eventDetail.data.providerBetslipId,
          });
          break;
        case 'onReBetRequested':
          ticketAPI.checkTicketByProviderBetslipId(eventDetail.data.providerBetslipId).then((response) => {
            const ticket = response.data.message;
            const bets = terminalTicketHelper.mapRebet(ticket);
            const { payin } = ticket;
            this.rebet({ bets, payin }).then(() => {
              eventBus.$emit('TicketRebet');
              this.setTicketToRebet(null);
            });
          });
          // Make ticket check request with provided providerBetslipId
          break;
        default:
          break;
      }
    });
    window.addEventListener('visibilitychange', () => {
      if (this.isMobile || this.isTablet) {
        this.getLastTickets();
      }
    });
    eventBus.$on('TicketPayin', this.isTerminalChannel ? this.ticketPayinTerminal : this.ticketPayin);
    eventBus.$on('TicketCancel', (ticket) => {
      busService.sendMessageAsync('Tickets.Cancel', {
        ticket,
      });
    });
    eventBus.$on('TicketPayout', (ticket) => {
      busService.sendMessageAsync('Tickets.Payout', {
        ticket,
      });
    });
    eventBus.$on('ReturnToLobby', () => {
      busService.sendMessage('UI.Show', {
        name: 'Lobby',
      });
      if (this.isAndroidPlatform) {
        window.WebAppListener.sendMessage('Navigation.Back');
      }
    });
    eventBus.$on('LoginRequired', () => {
      busService.sendMessage('User.LoginRequired', {
        type: 'loginRequired',
      });
      if (this.isAndroidPlatform) {
        window.WebAppListener.sendMessage('User.LoginRequired');
        // Playtech requires this on Android platform
        this.checkForRedirect();
      }
      integrator.sendMessage({
        type: 'loginRequired',
      });
    });
    eventBus.$on('TicketRebet', this.updateTicketDataAfterRebet);
    eventBus.$on('LoadLastTickets', this.getLastTickets);
    eventBus.$on('CheckTicket', (requestUuid) => this.checkTicket(requestUuid));
    eventBus.$on('RebetTicket', (ticket) => {
      busService.sendMessage('UI.Show', {
        name: ['BettingArea'],
      });
      this.setTicketToRebet(ticket);
    });
    eventBus.$on('CloseTicketDetails', () => {
      this.setIsLoaded(false);
      this.clearTicketDetailsData();

      if (this.isThirdPartyIntegration) this.setActiveComponent('BettingArea');
      else
        busService.sendMessage('UI.Show', {
          name: ['BettingArea'],
        });
    });
    eventBus.$on('TicketCancelled', async (ticketId) => {
      this.ticketCanceled = true;
      this.canceledTicket = { id: ticketId };
      this.getLastTickets();
      setTimeout(async () => {
        if (this.nextgenStandaloneIntegration) {
          await window.igniteInstance.getWallets().then((result) => {
            if (result.activeCurrencyBalance) {
              this.setUserBalance(result.activeCurrencyBalance);
            }
          });
        }
      }, 1000);
    });
    eventBus.$on('StopTicketCheck', this.clearCheckTimeout(this.check));
  },
  methods: {
    ...mapActions([
      'getResults',
      'getStats',
      'setUserBalance',
      'setGameState',
      'updateRound',
      'bettingDisable',
      'updateGameState',
      'clearSelection',
      'setIsBettingDisabled',
      'removeUser',
      'setTerminalUserData',
      'setTicketDetailsData',
      'clearTicketDetailsData',
      'setIsLoaded',
      'ticketDetailsRecheck',
      'setCurrentEventId',
      'setNextRoundTime',
      'setRoundInProgress',
      'timerTick',
      'setTicketToRebet',
      'toggleFreeBetModal',
      'toggleFreeBetExpiredModal',
      'setJackpotState',
      'updateConfig',
      'setPrintedTicketByTicketCheck',
    ]),
    ...mapActions('user', ['setUserFreeBets', 'getUserFreeBets', 'getUserBalance', 'updateUser']),
    ...mapActions('notifications', ['setNotification', 'clearNotification']),
    ...mapActions('gamesBetslip', [
      'setRoundNumber',
      'setIsPayinButtonDisabled',
      'getLastTickets',
      'setIsPayinInProgress',
      'clearBetslip',
      'updateBet',
      'cancelTicket',
      'rebet',
      'payIn',
      'ticketCheck',
      'removeBets',
    ]),
    closeCancelTicketModal() {
      this.ticketCanceled = false;
    },
    setTheme() {
      const rootElement = document.documentElement;
      const { config } = this.config.ui;
      const baseColor = this.config.ui[config.baseColor];
      const theme = this.config.ui[config.theme];
      applyPalette.set(merge(theme, baseColor), rootElement);
    },
    load() {
      if (this.isAndroidPlatform) {
        window.WebAppListener.sendMessage('Slave.Loaded');
      }
      busService.sendMessage('Slave.Loaded', true);
    },
    authUser(auth) {
      this.updateUser({
        auth: {
          token: auth.token,
          tpToken: auth.tpToken,
        },
        profile: auth.user || auth,
      });
      this.isLoggedIn = !!auth?.user?.logged || auth.id || !!auth?.user?.id;
      if (this.isLoggedIn) {
        if (window.WebAppListener) {
          this.getUserFreeBets();
        }
        if (!this.isTerminal) {
          this.getLastTickets();
        }
      }
    },
    setupGtag(device) {
      if (this.config.ui.config.gtm && this.config.ui.config.gtm[device]) {
        const { code, auth, preview } = this.config.ui.config.gtm[device];
        gtag.setup(code, auth, preview);
        return;
      }
      gtag.setup();
    },
    updateTicketRound() {
      if (!this.ticket || !this.ticket.length) return;
      this.ticket.forEach((bet) => {
        this.setRoundNumber({
          betId: bet.id,
          roundNumber: this.roundId,
        });
      });
    },
    formatTicketBets() {
      const tickets = this.ticket.length ? this.ticket : [this.selectedBet];
      const result = tickets.map((n) => ({
        payin: n.stake ? Number(Number(n.stake).toFixed(2)) : Number(this.totalPayment),
        type: n.betId,
        value: n.category === 'Special' && n.betId !== 23 ? n.outcomeIndex : n.cleanOutcome,
        numEvents: n.numEvents,
      }));
      return result;
    },
    getPrintTemplate() {
      let template = '';
      const messages = this.translations;
      const { currency } = this.config;
      const bets = this.ticket.length ? this.ticket : [this.selectedBet];
      template += 'LuckyX;';
      template += "{{ ticketDateTimeUTC | date('d.m.Y H:i:s', false) }};";
      template += `${messages.general_pay_in} {{ payin | number_format(2, '.', ',') }}${currency} `;
      template += `${messages.general_payout} {{ payout | number_format(2, '.', ',') }}${currency} `;

      return (
        template +
        bets
          .map((bet) => {
            const betOutcome = isArray(bet.outcome) ? bet.outcome.join(',') : bet.outcome;
            const betMarket = !bet.color ? `${bet.market} ${bet.category ? bet.category : ''}` : bet.category;
            let body = '';
            let round = '';

            for (let i = 0; i < bet.numEvents; i += 1) {
              round = `${messages.general_round} : {{bets[0].eventId + ${i}}}`;
              body += `${round} `;
              body += `${betOutcome} `;
              body += `${betMarket} `;
            }
            return body;
          })
          .join('; ') +
        '{{ id }}'
      );
    },
    clearCheckTimeout(timeout) {
      clearTimeout(timeout);
    },
    checkTicket(requestUuid, action) {
      this.check = setTimeout(async () => {
        try {
          const response = await this.ticketCheck(requestUuid);
          const { data } = response;
          if (data.status.value.toUpperCase() !== 'PENDING') {
            if (this.isTerminalChannel) {
              // If ticket details are open, show ticket after action is complete
              if (this.ticketDetailsData) this.setTicketDetailsData(data);

              const newTicket = cloneDeep(data);
              newTicket.action = action;

              this.busService.sendMessage(
                {
                  action: 'Tickets.Update',
                  enforceEvent: true,
                  data: {
                    action,
                    ticketData: newTicket,
                    showMessage: false,
                  },
                },
                true,
              );
              if (action === 'Cancel' && (data.status.value !== 'CLOSED' || !this.isThirdPartyIntegration)) return;
              if (
                action === 'Payout' &&
                data.status.value !== 'PAYEDOUT' &&
                (!this.isThirdPartyIntegration || !this.userIsOperator)
              )
                return;
              if (
                // eslint-disable-next-line max-len
                this.printedTicketsByTicketCheck.some(
                  (printedTicket) =>
                    printedTicket.requestUuid === data.requestUuid && printedTicket.status.value === data.status.value,
                )
              )
                return;
              this.setPrintedTicketByTicketCheck(newTicket);
              terminalTicketHelper.handleTerminalTicket(newTicket);
            } else {
              const message = {
                message: this.translations.general_ticket_confirmed,
                status: 'positive',
                notificationTimeout: 3000,
              };
              if (!this.isTerminalChannel) {
                this.getLastTickets();
                this.clearAndSetNotification(message);
              } else {
                this.showTerminalNotification(message);
                busService.sendMessage('Tickets.Update', {
                  action: 'Add',
                  ticketData: response.data,
                  showMessage: false,
                });
                this.getLastTickets();
              }
            }
            this.setIsPayinButtonDisabled(false);
            this.bettingDisable(false);
            this.clearBetslip();
            this.setIsPayinInProgress(false);
          }
          this.clearCheckTimeout(this.check);
          this.clearCheckTimeout(this.payinTimoutWithoutTicketCheck);
        } catch (error) {
          const notification = {
            type: 'warning',
            message: error.message,
          };

          if (this.isTerminalChannel) {
            this.showTerminalNotification({ ...notification, delay: 3000 });
          } else {
            this.bettingDisable(false);
            this.clearAndSetNotification({ ...notification, notificationTimeout: 3000 });
          }
        } finally {
          this.setIsPayinButtonDisabled(false);
          this.setIsPayinInProgress(false);
        }
      }, this.checkTimeout);
    },
    checkForRedirect() {
      const { clientplatform } = this.config;
      const redirectUrl = this.config.company.redirect.url;
      if (clientplatform && clientplatform === 'download') {
        window.location.replace(redirectUrl);
      }
    },
    updateTicketDataAfterRebet() {
      this.updateTicketRound();
      this.formatRebetTicket();
    },
    formatRebetTicket() {
      this.ticket.forEach((n) => {
        const bet = n;
        // Bet outcome comes from backend as a String with 2 spaces between values
        bet.outcome = bet.outcome.replaceAll('  ', ' ');
        bet.category = bet.type < 20 ? 'Standard' : 'Special';
        bet.cleanOutcome = bet.type !== 23 ? bet.outcome.split(' ') : Number(bet.outcome.split(' '));
        bet.betId = bet.type;

        // Update bet color
        const sameColor = bet.type !== 23 && bet.cleanOutcome.every((num) => num % 5 === bet.cleanOutcome[0] % 5);
        if (bet.cleanOutcome.length === 10 && sameColor && bet.type <= 10) {
          const hex = this.getNumberColor(bet.cleanOutcome[0]);
          const colorKey = this.bettingRules.bingoColors.findIndex((color) => color === hex);

          bet.market = this.translations[`luckyx_color_${colorKey}`];
          bet.color = hex;
        }

        this.updateBet(bet);
      });
    },
    toggleDeviceChannel(deviceId) {
      busService.addChannel('Device', deviceId);
    },
    togglePlayerChannel(userId) {
      if (userId) {
        busService.addChannel('Player', userId);
      } else {
        busService.removeChannel('Player', this.user.profile.id);
      }
    },
    addAjsChannel() {
      if (this.jackpotState) {
        busService.addChannel('Jackpot', this.jackpotState.jackpotId);
      }
    },
    setPageTitle() {
      const { productFqn, applicationName } = this.config;
      document.title = `${startCase(productFqn)}: ${applicationName}`;
    },
    claimFreeBets() {
      this.clearBetslip();
      this.setUserFreeBets(this.userFreeBets);
      this.toggleFreeBetModal(false);
    },
    resetUserFreeBets() {
      this.userFreeBets = {
        count: 0,
        betStake: 0,
      };
    },
    async clearAndSetNotification(notification) {
      await this.clearNotification();
      this.setNotification(notification);
    },
    handleSetState(eventDetail) {
      if (this.isTerminalLayout) {
        const countdownRequired = ['countdown', 'bettingDisabled'];
        if (countdownRequired.indexOf(eventDetail.type) !== -1) {
          const startingIn = eventDetail.delay - Math.round((eventDetail.sentTime - eventDetail.time) / 1000);
          this.setNextRoundTime(startingIn);
        } else {
          this.setRoundInProgress(true);
        }
      }

      if (eventDetail.eventId) {
        this.setCurrentEventId(eventDetail.eventId);

        if (this.userLogged) {
          this.getUserFreeBets();
          this.getLastTickets();
        }
      }

      if (eventDetail.type) {
        this.setGameState(eventDetail);
      }
      if (eventDetail.type === 'countdown' || eventDetail.type === 'bettingDisabled' || eventDetail.type === 'new') {
        this.updateRound(eventDetail.eventId);
      } else if (eventDetail.type === 'draw' || eventDetail.type === 'results') {
        this.updateRound(eventDetail.eventId + 1);
      }
    },
    handleStartEvent(eventDetail) {
      if (this.isTerminalLayout) {
        // If terminal ticket details are opened, recheck ticket to update it on start of the round
        this.ticketDetailsRecheck('0');
        this.setRoundInProgress(true);
      }
      this.setCurrentEventId(eventDetail.eventId);

      this.setGameState(eventDetail);
      this.updateRound(eventDetail.eventId + 1);
      this.updateTicketRound();
      this.setIsPayinButtonDisabled(false);
      if (this.userLogged && !this.isTerminalChannel) {
        setTimeout(() => {
          this.getLastTickets();
        }, 500);
      }
    },
    drawResult(eventDetail) {
      const currentBallEventId = eventDetail.eventId;
      const currentBallId = eventDetail.id;
      const currentBalls = clone(this.gameState.balls);
      const isCurrentBallMissing = currentBalls?.findIndex((n) => n.id === currentBallId) < 0;

      if (isCurrentBallMissing) {
        currentBalls.push({
          ball: eventDetail.ball,
          id: eventDetail.id,
          eventId: currentBallEventId,
        });

        this.updateGameState({
          ball: eventDetail.ball,
          currentBallId,
          balls: currentBalls,
          eventId: currentBallEventId,
          eventName: eventDetail.eventName,
          sentTime: eventDetail.sentTime,
        });
      }
    },
    handleAuthorizationChanged(eventDetail) {
      if (this.isTerminalChannel) {
        this.setTerminalUserData(eventDetail.data);
        return;
      }
      const authData = eventDetail.data.auth;
      if (authData) {
        const playerId = authData?.user?.id || null;
        this.togglePlayerChannel(playerId);
        this.authUser(authData);
      }
      if (authData.user && authData.user.id) {
        if (!this.config.user || !this.config.user.id) {
          this.setUserBalance(authData.user.balance);
        }
      } else {
        this.clearBetslip();
        this.removeUser();
      }
      if (this.isNextGen) {
        this.updateUser({ auth: eventDetail.data?.auth, profile: eventDetail.data?.auth?.user });
      }
    },
    handleTicketUpdate(eventDetail) {
      let notification = { notificationTimeout: 3000 };
      if (this.userLogged && !this.isTerminalChannel) {
        if (eventDetail.id && eventDetail.status.id === '0') {
          let message = this.translations.general_ticket_confirmed;
          let status = 'positive';
          notification.message = message;
          notification.status = status;

          // eslint-disable-next-line no-case-declarations
          const ticketsResolved = {
            action: 'Tickets.Resolved',
            data: {
              accepted: true,
              ticket: eventDetail,
            },
          };
          // Send analytics message to parent frames
          if (this.isAndroidPlatform) {
            window.WebAppListener.sendMessage(JSON.stringify(ticketsResolved));
          }
          busService.sendMessage(ticketsResolved.action, ticketsResolved.data);
          this.getUserFreeBets();
        }
        if (eventDetail.id && eventDetail.status.id === '1') {
          notification.message = this.translations.general_ticket_canceled;
          notification.status = 'neutral';
          this.ticketCanceled = true;
          this.canceledTicket = eventDetail;
          eventBus.$emit('RefreshTicketHistory');
        }
        if (this.isStandAlone && !this.isGravityGateway) {
          this.getUserBalance();
        }

        if (eventDetail.code) {
          notification.message = eventDetail.message;
          notification.status = 'negative';
          if (this.ticket.length === 1 && this.isQuickpayEnabled) {
            this.clearBetslip();
          }
        } else {
          this.removeBets();
        }
        this.getLastTickets();
        eventBus.$emit('SwitchToBetslipTab');
        this.clearAndSetNotification(notification);
      }
      if (this.isTerminalLayout) {
        const ticket = JSON.parse(JSON.stringify(eventDetail));
        ticket.ticketPin = eventDetail.ticketPin;
        ticket.translation = 'Lucky X';
        // To capitalize ticket action value
        const action = ticket.action.charAt(0).toUpperCase() + ticket.action.slice(1);
        busService.sendMessage('Tickets.Update', {
          action: action,
          ticketData: ticket,
          showMessage: false,
        });
        terminalTicketHelper.handleTerminalTicket(ticket, eventDetail);
        if (eventDetail.code) {
          terminalTicketHelper.handleTicketUpdateError(ticket, eventDetail.message);
        }
      }
      this.setIsPayinButtonDisabled(false);
      this.setIsPayinInProgress(false);
      this.bettingDisable(false);
      this.clearCheckTimeout(this.check);
      this.clearCheckTimeout(this.payinTimoutWithoutTicketCheck);
      this.clearBetslip();
    },
    handleStopBetting() {
      this.clearAndSetNotification({
        message: this.translations.general_betting_disabled,
        status: 'negative',
        notificationTimeout: 3000,
      });
      this.setIsPayinButtonDisabled(true);
      this.setIsBettingDisabled(true);
    },
    handleSetResults(eventDetail) {
      this.setGameState(eventDetail);
      this.getResults();
      this.getStats();
      if (this.userLogged || this.user.profile.logged) {
        setTimeout(async () => {
          if (this.nextgenStandaloneIntegration) {
            await window.igniteInstance.getWallets().then((result) => {
              if (result.activeCurrencyBalance) {
                this.setUserBalance(result.activeCurrencyBalance);
              }
            });
          }
        }, 5000);
      }
    },
    handleStartCountdown(eventDetail) {
      if (this.isTerminalLayout) {
        // If terminal ticket details are opened, recheck ticket to update it on end of the round
        this.ticketDetailsRecheck('8');
        this.setNextRoundTime(eventDetail.delay);
        this.setRoundInProgress(false);
      }

      if (this.userLogged || this.user.profile.logged) {
        this.getLastTickets();
        if (this.isStandAlone && !this.isGravityGateway) {
          this.getUserBalance();
        }
      }
    },
    async ticketPayin() {
      this.setIsPayinButtonDisabled(true);
      this.setIsPayinInProgress(true);
      this.bettingDisable(true);
      try {
        const response = await this.payIn({
          payload: this.formatTicketBets,
          additionalInfo: this.isIsonis
            ? {
                printTemplate: this.getPrintTemplate(),
                timezone: {
                  offset: parseInt(this.user.profile.profile.timezoneOffset, 10),
                },
              }
            : null,
          providerBetslipId: ulid() || null,
        });
        await this.clearBetslip();
        if (this.isNextGen && response.status === 201) {
          this.setIsPayinButtonDisabled(false);
          this.setIsPayinInProgress(false);
          this.bettingDisable(false);
          const message = {
            message: this.translations.general_ticket_confirmed,
            status: 'positive',
            notificationTimeout: 3000,
          };
          this.clearAndSetNotification(message);
          this.getLastTickets();
          this.getUserFreeBets();
          if (this.nextgenStandaloneIntegration) {
            await window.igniteInstance.getWallets().then((result) => {
              if (result.activeCurrencyBalance) {
                this.setUserBalance(result.activeCurrencyBalance);
              }
            });
          }
        } else if (this.isSevenPlatform) {
          const message = {
            message: this.translations.general_ticket_resolving,
            status: 'neutral',
          };
          this.setNotification(message);
          this.checkTicket(response.data.requestUuid);
        } else {
          const message = {
            message: this.translations.general_ticket_resolving,
            status: 'neutral',
          };
          this.setNotification(message);
          // If ticket update is missing and no ticket check api is available (playtech)
          // loader should be cleared and generic error message displayed
          this.payinTimoutWithoutTicketCheck = setTimeout(() => {
            const message = {
              message: this.translations.general_ticket_error,
              status: 'negative',
              notificationTimeout: 3000,
            };
            this.setNotification(message);
            this.setIsPayinInProgress(false);
            this.bettingDisable(false);
            this.getLastTickets();
          }, this.checkTimeout);
        }
      } catch (error) {
        if (error.response) {
          const errorData = error.response.data;
          const message = {
            message: errorData.message,
            status: 'negative',
            notificationTimeout: 3000,
          };
          this.setNotification(message);
        }

        this.setIsPayinButtonDisabled(false);
        this.setIsPayinInProgress(false);
        this.bettingDisable(false);
      }

      this.clearSelection();
    },
    async ticketPayinTerminal() {
      this.setIsPayinButtonDisabled(true);
      this.setIsPayinInProgress(true);
      this.bettingDisable(true);
      try {
        const response = await this.payIn({
          payload: this.formatTicketBets,
          additionalInfo: this.isIsonis
            ? {
                printTemplate: this.getPrintTemplate(),
                timezone: {
                  offset: parseInt(this.user.profile.profile.timezoneOffset, 10),
                },
              }
            : null,
          providerBetslipId: ulid() || null,
        });

        await this.clearBetslip();
        if (window.innerWidth <= 1260) eventBus.$emit('MinimizeBetslip');

        const config = {
          ticketGroup: this.config.productName,
        };
        const formatedPayload = response;
        formatedPayload.ticket.stake = response.ticket.payin;
        formatedPayload.ticket.config = config;
        if (formatedPayload.ticket.payin > this.user.balance) {
          busService.sendMessage('Dialog.Show', {
            message: this.translations.player_balance_insufficient,
            type: 'warning',
            delay: 3000,
          });
          return;
        }
        busService.sendMessageAsync('Tickets.Pay', formatedPayload);
      } catch (error) {
        if (error.response) {
          const errorData = error.response.data;
          const message = {
            message: errorData.message,
            status: 'negative',
            notificationTimeout: 3000,
          };
          this.setNotification(message);
        }

        this.setIsPayinButtonDisabled(false);
        this.setIsPayinInProgress(false);
        this.bettingDisable(false);
      }

      this.clearSelection();
    },
  },
};
</script>

<style lang="scss">
@use './assets/style/helpers/_modal.scss';
* {
  margin: 0;
  padding: 0;
  outline-style: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
}
html,
body {
  background-color: var(--bg-color-1);
  font-family: 'Roboto', sans-serif;
  box-sizing: border-box;
  -ms-overflow-style: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  overflow: auto;
  -webkit-text-size-adjust: 100%; /* Prevent font scaling */
  text-size-adjust: 100%;
}
*,
*:before,
*:after {
  box-sizing: inherit;
}
#app {
  font-family: 'Roboto', sans-serif;
}
.app {
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  height: 100%;
  overflow: auto;
  // default style
  color: var(--text-primary-1);
  font-size: var(--font-size-medium, 14px);

  &.background {
    &.dark {
      background-image: url('assets/images/game-bg-dark.jpg');
    }

    &.light {
      background-image: url('assets/images/game-bg-light.jpg');
    }
  }

  &.hide-cursor {
    * {
      cursor: none !important;
    }
  }

  .cancel-ticket-confirm,
  .free-bet-expired-modal,
  .free-bet-modal {
    .modal {
      background-color: var(--card);

      .modal-header,
      .modal-body {
        color: var(--text-primary-1);
      }

      .modal-header {
        font-size: 16px;
      }

      .modal-body {
        font-size: 14px;
        .modal-actions {
          display: none;
        }
      }
      .modal-footer {
        padding-right: 43px;
        position: relative;
      }
      .button {
        background: transparent;
        border: 0;
        outline: 0;
        font-size: 14px;
        color: var(--secondary);
        text-transform: uppercase;
        cursor: pointer;
        font-family: 'Roboto', sans-serif;
        padding: 31px 0 27px 0;
      }
    }
  }
}

.free-bet-modal {
  .modal {
    max-height: 348px !important;
    .modal-header {
      display: none;
    }
    .modal-body {
      height: 348px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      background-position: 0 0;
      background-repeat: no-repeat;
      position: relative;
      text-align: center;
      img {
        position: absolute;
        top: -2%;
        left: 22px;
        max-width: 100%;
      }
      .body-message-box {
        position: absolute;
        width: 100%;
        top: 66%;
        left: 50%;
        transform: translate(-50%, -50%);

        .title {
          font-size: 42px;
          font-weight: 700;
          color: #90b653;
          margin-bottom: 25px;
          text-transform: uppercase;
        }
        .subtitle {
          font-size: 18px;
          font-weight: 700;
        }
        .subtitle-2 {
          font-size: 14px;
          color: var(--text-primary-3);
        }
      }
    }
    .modal-footer {
      display: none;
    }
  }
  &.desktop {
    .modal {
      .modal-body {
        background-image: none;
        img {
          height: 60%;
          left: 50%;
          top: -2%;
          transform: translate(-50%, 0);
        }
        .body-message-box {
          top: 60%;
          .title {
            margin-bottom: 10px;
          }
          .pre-title {
            display: none;
          }
        }
        .action {
          position: absolute;
          bottom: 0;
          right: 16px;
        }
      }
    }
  }
  &.mobile {
    .modal {
      height: 70% !important;
      .modal-body {
        height: 100% !important;
        background-image: url('assets/images/free-bet-bg.svg');
        .action {
          position: absolute;
          left: 0;
          bottom: 20px;
          display: flex;
          width: 100%;
          justify-content: center;
          align-items: center;
          .button {
            min-width: 150px;
            background: #90b653;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 30px;
            padding: 0;
            color: var(--text-secondary-1);
            font-weight: 500;
          }
        }
      }
    }
  }
}

@media (max-width: 660px) {
  .app {
    .cancel-ticket-confirm,
    .free-bet-expired-modal {
      background-color: var(--overlay-lock);
      .modal {
        background-color: var(--card);
        color: var(--text-primary-1);
        height: 182px !important;

        .modal-header {
          background-color: var(--card);
          padding: 24px 24px 30px 24px;
        }

        .modal-body {
          position: relative;
          padding: 0 24px;
          .modal-actions {
            display: block;
            position: absolute;
            bottom: 0;
            right: 0;
            padding-right: 43px;
          }
        }

        .modal-footer {
          display: none;
        }
      }
    }
  }
}

@media (-webkit-device-pixel-ratio: 1.1) {
  .ticket-wrapper {
    zoom: 0.9;
  }
}

@media (-webkit-device-pixel-ratio: 1.25) {
  .ticket-wrapper {
    zoom: 0.8;
  }
}
</style>
